import api from '../../utils/api'
import { useState, useEffect, useMemo } from 'react'
import Select from 'react-select'
import { FiInbox } from 'react-icons/fi'

import Input from '../../shared/components/form/input'
import Button from '../../shared/components/Button/Button'
import Modal from '../../shared/modal/Modal'
import selectStyles from '../../shared/css/SelectStyle'
import buildCategoryOptions from '../../shared/functions/buildCategoryOptions'
import { FINANCE_STATUS_LABEL } from '../../shared/constants/financePaymentMethods'

import ListFinanceEntryRow from './ListRow/ListFinanceEntryRow'
import FormFinanceEntry from './Form/FormFinanceEntry'

import useMyPermission from '../../hooks/useMyPermission'

import './Finance.css'

const STATUS_OPTIONS = Object.entries(FINANCE_STATUS_LABEL).map(([value, label]) => ({ value, label }))

// Contas a Pagar e a Receber são a mesma tela, parametrizada por type — mesmo padrão já usado
// em SectorQueue.js (Produção) pra Corte/Costura/Acabamento/etc.
function EntryList({ type, title }) {
    const [token] = useState(localStorage.getItem('token') || '')

    const [entries, setEntries] = useState([])
    const [categories, setCategories] = useState([])
    const [load, setLoad] = useState(false)
    const [modalNew, setModalNew] = useState(false)

    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState(null)
    const [categoryFilter, setCategoryFilter] = useState(null)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: '' })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, 'finance_view')
    const myPermissionCreate = checkPermission(myPermission, 'finance_create')
    const myPermissionEdit = checkPermission(myPermission, 'finance_edit')
    const myPermissionDelete = checkPermission(myPermission, 'finance_delete')

    const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories])

    const getAll = () => {
        Promise.all([
            api.get(`/finance/entry/getall?type=${type}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/finance/category/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
        ]).then(([entryRes, categoryRes]) => {
            setEntries(entryRes.data.entries || [])
            setMyPermission(entryRes.data.permission)
            setCategories(categoryRes.data.categories || [])
            setLoad(true)
        })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    const filter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        return entries.filter((e) => {
            if (statusFilter && e.status !== statusFilter.value) return false
            if (categoryFilter && !e.allocations.some((a) => (a.category?._id || a.category) === categoryFilter.value)) return false
            if (search) {
                const haystack = `${e.description} ${e.counterpart?.name || ''}`.toLowerCase()
                if (!haystack.includes(searchLowerCase)) return false
            }
            return true
        })
    }, [entries, search, statusFilter, categoryFilter])

    if (!load) {
        return <div className="pg-loading">Carregando {title.toLowerCase()}...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">{title}</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionCreate && (
                                <Button variant="new" onClick={() => setModalNew(true)}>Novo Lançamento</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <div style={{ minWidth: 160 }}>
                                <Select options={STATUS_OPTIONS} styles={selectStyles} value={statusFilter} onChange={setStatusFilter} isClearable placeholder="Status..." menuPortalTarget={document.body} menuPosition="fixed" />
                            </div>
                            <div style={{ minWidth: 200 }}>
                                <Select options={categoryOptions} styles={selectStyles} value={categoryFilter} onChange={setCategoryFilter} isClearable placeholder="Categoria..." menuPortalTarget={document.body} menuPosition="fixed" />
                            </div>
                            <div className="pg-search">
                                <Input type="text" name="search" placeholder="Buscar por descrição ou contraparte..." value={search} handleOnChange={(e) => setSearch(e.target.value)} />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'lançamento' : 'lançamentos'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListFinanceEntryRow
                        dataArray={filter}
                        type={type}
                        categories={categories}
                        myPermissionEdit={myPermissionEdit}
                        myPermissionDelete={myPermissionDelete}
                        handleOnSuccess={getAll}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum lançamento encontrado</p>
                        <p className="pg-empty-subtitle">
                            {entries.length === 0
                                ? 'Lançamentos aparecem aqui automaticamente, ou cadastre um novo.'
                                : 'Tente ajustar os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                w={70}
                content={
                    <FormFinanceEntry
                        type={type}
                        categories={categories}
                        btCancelAction={() => setModalNew(false)}
                        handleOnSuccess={() => { setModalNew(false); getAll() }}
                    />
                }
            />

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar o Financeiro</p>
        </div>
    )
}

export default EntryList
